import { useCallback } from 'react';

import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { isDocumentProjectType, isLongFormProjectType, PROJECT_TYPE } from 'src/data/project';
import { type VoidFunction } from 'src/lib/types';
// @ts-expect-error - useCreateProject doesn't have TypeScript definitions
import { useCreateProject } from 'src/services/useCreateProject';
// @ts-expect-error - useProjectTemplatePopupSubmitHandler doesn't have TypeScript definitions
import { useProjectTemplatePopupSubmitHandler } from 'src/services/useProjectTemplatePopupSubmitHandler';
import { useCustomerSfAiChatEnabled } from 'src/store/customer/useCustomerSfAiChatEnabled';
import {
  PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB,
  useProjectTemplatePopup,
  // @ts-expect-error - ProjectTemplatePopup doesn't have TypeScript definitions
} from 'src/views/ProjectTemplatePopup';

import { useEvents } from './services/useEvents';
import {
  QUICK_NAVIGATION_EVENT,
  WIDGET_ACTION,
  type WidgetAction,
  type WidgetActionData,
  type WidgetActionHandler,
} from './lib';
import { useMainActions } from './store';

interface Params {
  onFlowFinished?: VoidFunction;
  inPopupMode?: boolean;
}

export const useHandlers = ({ onFlowFinished, inPopupMode }: Params) => {
  const createProject = useCreateProject();
  const openProjectTemplatePopup = useProjectTemplatePopup();
  const handleProjectTemplatePopupSubmit = useProjectTemplatePopupSubmitHandler();
  const aiChatEnabled = useCustomerSfAiChatEnabled();
  const events = useEvents({ inPopupMode });
  const mainActions = useMainActions();

  const handleWidgetAction: WidgetActionHandler = useCallback(
    async (action: WidgetAction, data?: WidgetActionData) => {
      switch (action) {
        /**
         * Ask user if to open a new "short form" or "long form" content and redirect to general prompt / blank template
         */
        case WIDGET_ACTION.createBlankProject: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.newBlankProject);

          const projectType = mainActions.getBlankProjectType();
          events.trackBlankProject(projectType);

          const creationParams: { generationTool?: GenerationTool } = {};
          if (isLongFormProjectType(projectType)) {
            creationParams.generationTool = GENERATION_TOOL.longFormBlank;

            // if Agent is not enabled for customer yet - start "short-form" blank project from "General prompt" tool
          } else if (!aiChatEnabled && isDocumentProjectType(projectType)) {
            creationParams.generationTool = GENERATION_TOOL.freestyle;
          }
          createProject(projectType, creationParams);

          onFlowFinished?.();
          break;
        }

        /**
         * Process "recently used template card" click - create a new project based on the card's template and sub-template (if applicable)
         */
        case WIDGET_ACTION.createRecentProject: {
          const cardData = data?.recentlyUsedTemplateCardData;
          if (!cardData) {
            return;
          }

          const params: Record<string, unknown> = {
            generationTool: cardData.generationTool,
          };
          if (cardData.customToolData) {
            params.customMultiPromptId = cardData.customToolData.id;
            params.customMultiPromptGroup = cardData.customToolData.templateGroup;
          }
          if (cardData.subTemplateData) {
            params.subTemplateId = cardData.subTemplateData.id;
          }

          createProject(cardData.projectType, params);
          onFlowFinished?.();
          break;
        }

        /**
         * Create a new "long form builder" project with "long form localization" tool
         */
        case WIDGET_ACTION.createLocalizationProject: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.newLocalizationProject);

          createProject(PROJECT_TYPE.longFormBuilder, {
            generationTool: GENERATION_TOOL.longFormLocalization,
          });
          onFlowFinished?.();
          break;
        }

        /**
         * Create a new "blog wizard" project
         */
        case WIDGET_ACTION.createBlogProject: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.newBlogProject);

          createProject(PROJECT_TYPE.blogPostBuilder);
          onFlowFinished?.();
          break;
        }

        /**
         * Open content improver menu to select a content type
         */
        case WIDGET_ACTION.improveContent: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.improveExistingContent);

          const projectType = mainActions.getImproveProjectType();

          if (projectType === PROJECT_TYPE.longFormBuilder) {
            createProject(PROJECT_TYPE.longFormBuilder, {
              generationTool: GENERATION_TOOL.longFormContentImprover,
            });
          } else {
            createProject(PROJECT_TYPE.document, {
              generationTool: GENERATION_TOOL.basicContentImprover,
            });
          }
          onFlowFinished?.();
          break;
        }

        /**
         * Open the template gallery on default tab (”Most popular”)
         */
        case WIDGET_ACTION.openTemplateGallery: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.templateGallery);

          openProjectTemplatePopup({
            selectedTabType: PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB.mostPopular,
            onSubmit: (...args: Record<string, unknown>[]) => {
              handleProjectTemplatePopupSubmit(...args);
              onFlowFinished?.();
            },
          });
          break;
        }
      }
    },
    [
      events,
      mainActions,
      aiChatEnabled,
      createProject,
      onFlowFinished,
      openProjectTemplatePopup,
      handleProjectTemplatePopupSubmit,
    ]
  );

  return {
    handleWidgetAction,
  };
};
